import { Box, ButtonBase, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { useRecoilValueLoadable } from "recoil";

import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { YearlyCashflowResultsOnSelectedPeriod } from "@/view_models/balancesheetSelectors";
import { ReducedYearlySimulationResults } from "@/view_models/simulationSelectors";

import { Loading } from "../Loading";

import { DetailRow, DetailSection } from "./DetailSection";

const useStyles = makeStyles({
  root: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr)",
    minWidth: 0,
    gap: layout.space[24],
    paddingBottom: layout.space[40],
  },
  description: { ...textStyles["SP/Body/Small"], color: colors["text/secondary"], marginBottom: layout.space[16] },
  answer: {
    padding: layout.space[16],
    borderRadius: layout.radius.md,
    background: colors["surface/base"],
    marginBottom: layout.space[24],
  },
  number: { ...textStyles["Number/L"], marginTop: layout.space[8] },
  chart: { overflowX: "auto", overflowY: "hidden" },
  legend: {
    display: "flex",
    gap: layout.space[16],
    justifyContent: "center",
    marginTop: layout.space[12],
    ...textStyles["SP/Body/Small"],
  },
  dot: { width: 8, height: 8, borderRadius: layout.radius.full, display: "inline-block", marginRight: layout.space[4] },
  yearPicker: {
    display: "flex",
    overflowX: "auto",
    gap: layout.space[4],
    marginTop: layout.space[16],
    paddingBottom: layout.space[8],
  },
  year: { minWidth: 56, minHeight: 44, borderRadius: layout.radius.md, ...textStyles["SP/Button/S"] },
  selection: {
    background: colors["surface/base"],
    borderRadius: layout.radius.md,
    padding: layout.space[16],
    marginTop: layout.space[16],
  },
});

// Data sources and annual amounts match the existing simulation charts.
export const SimulationCharts: React.FC = () => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const saleLoadable = useRecoilValueLoadable(ReducedYearlySimulationResults);
  const annualLoadable = useRecoilValueLoadable(YearlyCashflowResultsOnSelectedPeriod);
  const [selectedYear, setSelectedYear] = React.useState<number>();
  if (saleLoadable.state === "loading" || annualLoadable.state === "loading") return <Loading />;
  if (saleLoadable.state === "hasError") throw saleLoadable.contents;
  if (annualLoadable.state === "hasError") throw annualLoadable.contents;
  const data = annualLoadable.contents.map((element) => {
    const income = Math.round(
      element.result.rent_income +
        element.result.management_fee_income +
        element.result.security_deposit +
        element.result.key_money +
        element.sales_amount_by_selling
    );

    const expence = Math.round(
      element.result.management_cost +
        element.result.building_management_cost +
        element.result.repair_fund_cost +
        element.result.misc_cost +
        // 一棟物件の基本月額経費
        ((element.result.building_management_company_commission || 0) +
          (element.result.building_shared_utility_expenses || 0) +
          (element.result.building_misc_cost || 0)) +
        (element.result.loan_interest + element.result.loan_principal + element.result.loan_prepayment) +
        element.result.outsourcing_fee_of_new_lease_contract +
        element.result.outsourcing_fee_of_updating_lease_contract +
        element.result.large_scale_repair_fund_cost +
        (element.price_of_properties - element.price_of_loan) + // 頭金
        element.price_of_misc_expences_at_bought +
        element.property_acquisition_tax +
        (element.result.misc_onetime_cost +
          element.result.property_tax +
          element.result.fire_insurance_cost +
          element.result.earthquake_insurance_cost) +
        (element.expenses_by_selling + element.loan_prepayment_by_selling) +
        element.result.tax_on_real_estates
    );

    return {
      year: element.month.year(),
      income: income,
      expence: 0 - expence,
      total_balance: income - expence,
    };
  });

  const saleData = saleLoadable.contents.map((item) => ({
    ...item,
    price: Number(Object.values(item.market_prices)[0] ?? 0),
  }));
  const year = selectedYear ?? data[0]?.year;
  const annual = data.find((item) => item.year === year) ?? data[0];
  const sale = saleData.find((item) => item.year === year) ?? saleData[0];
  const onChartClick = (event: { activeLabel?: number | string }) => {
    if (event?.activeLabel != null) setSelectedYear(Number(event.activeLabel));
  };
  const ticks = saleData.filter((_, index) => index % (isMobile ? 2 : 4) === 0).map((item) => item.year);
  const chartWidth = isMobile ? Math.max(280, data.length * 20 + 56) : "100%";
  const axis = { stroke: colors["text/secondary"], fontSize: 10 };
  return (
    <Box className={classes.root}>
      <DetailSection title="売却した場合の手残り">
        <Typography className={classes.description}>
          物件価格とローン残高の差が大きいほど、手残りが多くなります。
        </Typography>
        <Box className={classes.answer}>
          <Typography variant="caption">{sale?.year}年の手残り</Typography>
          <Typography className={classes.number}>
            {Math.round(((sale?.price ?? 0) - (sale?.loan_amount ?? 0)) / 10000).toLocaleString()}
            <Box component="span" fontSize={14}>
              万円
            </Box>
          </Typography>
        </Box>
        <Box className={classes.chart}>
          <Box width={chartWidth}>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart
                data={saleData}
                margin={{ top: 8, right: 12, bottom: 0, left: 0 }}
                onClick={onChartClick}
                onMouseMove={!isMobile ? onChartClick : undefined}
              >
                <CartesianGrid vertical={false} stroke={colors["border/default"]} />
                <XAxis dataKey="year" ticks={ticks} tick={axis} tickLine={false} axisLine={false} />
                <YAxis
                  width={56}
                  tick={axis}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${Math.round(value / 10000).toLocaleString()}`}
                />
                <Area
                  type="monotone"
                  dataKey="price"
                  stroke={colors["chart/income-1"]}
                  fill={colors["chart/income-2"]}
                  fillOpacity={1}
                  isAnimationActive={false}
                />
                <Area
                  type="monotone"
                  dataKey="loan_amount"
                  stroke={colors["chart/other"]}
                  fill={colors["chart/baseline"]}
                  fillOpacity={1}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Box>
        <Box className={classes.legend}>
          <Box>
            <span className={classes.dot} style={{ background: colors["chart/income-2"] }} />
            物件価格
          </Box>
          <Box>
            <span className={classes.dot} style={{ background: colors["chart/baseline"] }} />
            ローン残高
          </Box>
          <Box>単位：万円</Box>
        </Box>
        <Box className={classes.selection}>
          <Typography variant="caption">{sale?.year}年</Typography>
          <DetailRow label="物件価格" value={`${Math.round((sale?.price ?? 0) / 10000).toLocaleString()}万円`} />
          <DetailRow
            label="ローン残高"
            value={`${Math.round((sale?.loan_amount ?? 0) / 10000).toLocaleString()}万円`}
          />
        </Box>
      </DetailSection>
      <DetailSection title="保有した場合の収支">
        <Typography className={classes.description}>
          将来的な年間キャッシュフローを予測できます。最後に登録した賃貸借契約期間が更新されていく想定です。
        </Typography>
        <Box className={classes.answer}>
          <Typography variant="caption">{annual?.year}年の年間収支</Typography>
          <Typography
            className={classes.number}
            style={{ color: (annual?.total_balance ?? 0) < 0 ? colors["text/error"] : colors["text/primary"] }}
          >
            {Math.round((annual?.total_balance ?? 0) / 10000).toLocaleString()}
            <Box component="span" fontSize={14}>
              万円
            </Box>
          </Typography>
        </Box>
        <Box className={classes.chart}>
          <Box width={chartWidth}>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={data}
                margin={{ top: 8, right: 12, bottom: 0, left: 0 }}
                onClick={onChartClick}
                onMouseMove={!isMobile ? onChartClick : undefined}
              >
                <CartesianGrid vertical={false} stroke={colors["border/default"]} />
                <XAxis dataKey="year" ticks={ticks} tick={axis} tickLine={false} axisLine={false} />
                <YAxis
                  width={56}
                  tick={axis}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${Math.round(value / 10000).toLocaleString()}`}
                />
                <Bar dataKey="total_balance" isAnimationActive={false} barSize={isMobile ? 12 : undefined}>
                  {data.map((item) => (
                    <Cell
                      key={item.year}
                      fill={item.total_balance < 0 ? colors["chart/expense-2"] : colors["chart/income-1"]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
        <Box className={classes.yearPicker} aria-label="グラフの年を選択">
          {data.map((item) => (
            <ButtonBase
              key={item.year}
              className={classes.year}
              style={{ background: item.year === year ? colors["state/selected-bg"] : colors["surface/base"] }}
              aria-pressed={item.year === year}
              onClick={() => setSelectedYear(item.year)}
            >
              {item.year}年
            </ButtonBase>
          ))}
        </Box>
        <Box className={classes.selection}>
          <Typography variant="caption">{annual?.year}年</Typography>
          <DetailRow label="収入" value={`${Math.round((annual?.income ?? 0) / 10000).toLocaleString()}万円`} />
          <DetailRow label="支出" value={`${Math.round((annual?.expence ?? 0) / 10000).toLocaleString()}万円`} />
          <DetailRow
            label="年間収支"
            value={`${Math.round((annual?.total_balance ?? 0) / 10000).toLocaleString()}万円`}
          />
        </Box>
      </DetailSection>
    </Box>
  );
};
